import React from 'react';
import PropTypes from 'prop-types';
import { Grid, TableCell, Typography, Tooltip } from '@material-ui/core';
import { styled, makeStyles } from '@material-ui/styles';

import { useHealthIntervals } from 'hooks';
import { useTranslation } from 'react-i18next';

import TableCellVertLine from './TableCellVertLine';
import ReadingsHelpers from 'utils/ReadingsHelpers';
import TelemetryHealthBar from './TelemetryHealthBar';

const NoPaddingTableCell = styled(TableCell)({
  padding: 0,
  border: 'none',
  textAlign: 'center',
});

const useStyles = makeStyles(() => ({
  healthyBottom: {
    paddingBottom: 5,
    marginBottom: 2,
  },
  unhealthyBottom: {
    marginBottom: -4,
  },
  emptyLine: {
    padding: 20,
  },
  toolTipHeader: {
    fontSize: '18px',
    fontFamily: 'Roboto',
    marginBottom: 15,
    marginTop: 10,
  },
  toolTipBody: {
    fontSize: '14px',
    fontFamily: 'Roboto',
    display: 'flex',
  },
  toolTipSubtitle: {
    fontSize: '10px',
    fontFamily: 'Roboto',
    display: 'flex',
  },
  toolTipSubtitleHeavy: {
    fontSize: '12px',
    fontFamily: 'Roboto',
    display: 'flex',
    fontWeight: 'bold',
  },
}));

const TelemetryTableCell = ({
  telemetryType,
  depth,
  value,
  error,
  getHealthStatus,
  showLineForEmpty,
  getInterval,
  convertPPM,
  convertTemp,
  convertUnits,
  uncertainty,
  final,
}) => {
  const { getSuggestionForInterval } = useHealthIntervals();
  const { t } = useTranslation();
  const convert = ReadingsHelpers.getNeedsConversion(telemetryType, convertPPM, convertTemp, convertUnits);
  const healthInterval = getInterval(telemetryType, depth);

  let convertedValue = value;
  if (ReadingsHelpers.readings[telemetryType]?.conversion) {
    convertedValue = ReadingsHelpers.readings[telemetryType]?.conversion(value);
  }
  const healthStatus = getHealthStatus(healthInterval, convertedValue);

  const getSuggestionTooltip = (healthInterval, healthStatus) => {
    if (!healthInterval || !healthStatus) {
      return undefined;
    }
    const suggestion = getSuggestionForInterval(healthInterval, healthStatus.bound);
    if (suggestion) {
      return (
        <div>
          <div className={classes.toolTipHeader}>
            <span>
              <b>{t(telemetryType)}</b> is <b className={classes[healthStatus.status]}>{t(healthStatus.status)}</b>
            </span>
          </div>
          <div className={classes.toolTipBody}>
            <b>{t('suggestion')} :</b>
          </div>
          <div className={classes.toolTipBody}>
            <p>{suggestion.suggestion}</p>
          </div>
          <div>
            <a style={{ alignSelf: 'center', marginBottom: 10, paddingLeft: '38%' }}>
              <span className={classes.toolTipSubtitleHeavy}>Reference:</span>{' '}
              <span className={classes.toolTipSubtitle}>{suggestion.reference}</span>
            </a>
          </div>
        </div>
      );
    }
  };

  const classes = useStyles();

  const display = ReadingsHelpers.displayReading(telemetryType, value, error, false, convert, uncertainty);
  const toolTipFragment = getSuggestionTooltip(healthInterval, healthStatus);

  return (
    <NoPaddingTableCell key={Math.random() * 10000}>
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        key={Math.random() * 10000}
        className={classes.unhealthyBottom}
      >
        <TableCellVertLine
          key={Math.random() * 10000}
          hideLine={!showLineForEmpty && !value}
          emptyLine={showLineForEmpty && !value}
        />
        {showLineForEmpty && !value && (
          <TableCellVertLine
            key={Math.random() * 10000}
            hideLine={!showLineForEmpty && !value}
            emptyLine={showLineForEmpty && !value}
          />
        )}
        {display ? (
          <div key={Math.random() * 10000}>
            <Grid
              container
              item
              className={classes.healthyBottom}
              direction="column"
              alignItems="center"
              key={Math.random() * 10000}
            >
              <Grid item>
                <Typography variant="h2" key={Math.random() * 10000} className={classes.healthyBottom}>
                  {display}
                </Typography>
              </Grid>
              <Grid item>
                {toolTipFragment ? (
                  <Tooltip title={<React.Fragment>{toolTipFragment}</React.Fragment>} arrow>
                    <div>
                      <TelemetryHealthBar bound={healthStatus?.bound} status={healthStatus?.status} />
                    </div>
                  </Tooltip>
                ) : (
                  <TelemetryHealthBar bound={healthStatus?.bound} status={healthStatus?.status} />
                )}
              </Grid>
            </Grid>
          </div>
        ) : (
          <TableCellVertLine
            key={Math.random() * 10000}
            hideLine={!showLineForEmpty && !value}
            emptyLine={showLineForEmpty && !value}
          />
        )}
        <TableCellVertLine
          key={Math.random() * 10000}
          hideLine={final || (!showLineForEmpty && !value)}
          emptyLine={showLineForEmpty && !value}
        />
      </Grid>
    </NoPaddingTableCell>
  );
};

TelemetryTableCell.propTypes = {
  healthIntervals: PropTypes.array,
  telemetryType: PropTypes.string,
  depth: PropTypes.number,
  value: PropTypes.number,
  error: PropTypes.string,
  getHealthStatus: PropTypes.func,
  getInterval: PropTypes.func,
  getSuggestion: PropTypes.func,
  showLineForEmpty: PropTypes.bool,
  final: PropTypes.bool,
  convertPPM: PropTypes.bool,
  convertTemp: PropTypes.bool,
  convertUnits: PropTypes.bool,
  uncertainty: PropTypes.number,
};

TelemetryTableCell.defaultProps = {
  healthIntervals: [],
  telemetryType: '',
  depth: 0,
  value: 0,
  error: '',
  getHealthStatus: () => {},
  getInterval: () => {},
  showLineForEmpty: false,
  final: false,
  convertPPM: false,
  convertTemp: false,
  convertUnits: false,
  getSuggestion: () => {},
  uncertainty: -1,
};

export default TelemetryTableCell;
